import React, { useState, useRef, useEffect } from "react";
import { makeStyles, ThemeProvider } from "@material-ui/core/styles";
import SwipeableDrawer from "@material-ui/core/SwipeableDrawer";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import SendIcon from "@material-ui/icons/Send";
import StopIcon from "@material-ui/icons/Stop";
import { Typography, Box, TextField, CircularProgress } from "@material-ui/core";
import themes from "../../palette";
import { createConversation, postTurn } from "../../api/copilotApi";

// Strip inline source/citation markers (e.g. "[test-doc.pdf]", "[1]", trailing "Sources:") from a reply.
function stripCitations(text) {
    if (!text) return "";
    return text
        .replace(/\s*\[[^\]\n]*\.(pdf|docx?|pptx?|xlsx?|txt|md|html?|csv)\]/gi, "")
        .replace(/\s*\[\d+(?:\s*,\s*\d+)*\]/g, "")
        .replace(/\n?\s*Sources?:[^\n]*/gi, "")
        .replace(/[ \t]{2,}/g, " ")
        .replace(/[ \t]+([.,;:])/g, "$1")
        .trim();
}

// Sanitize a link URL: allow only http(s)/mailto and relative URLs; block javascript:, data:,
// vbscript:, file: and any other scheme (defends against XSS via crafted CR/tool data).
function sanitizeUrl(url) {
    const raw = String(url || "");
    // Strip control chars / whitespace that browsers ignore when parsing a scheme
    // (e.g. "java\tscript:" -> "javascript:") before testing the scheme.
    const stripped = raw.replace(/[\u0000-\u0020]/g, "").toLowerCase();
    const schemeMatch = stripped.match(/^([a-z][a-z0-9+.-]*):/);
    if (schemeMatch) {
        const scheme = schemeMatch[1];
        if (scheme !== "http" && scheme !== "https" && scheme !== "mailto") return null;
    }
    return raw.trim();
}

// Render a small, safe subset of inline Markdown (links, bold, inline code) as React nodes.
function renderInline(text, keyPrefix) {
    const nodes = [];
    const regex = /\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|`([^`]+)`/g;
    let lastIndex = 0;
    let match;
    let i = 0;
    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
        if (match[1] !== undefined) {
            const href = sanitizeUrl(match[2]);
            if (href) {
                nodes.push(
                    <a
                        key={keyPrefix + "-a" + i}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {match[1]}
                    </a>
                );
            } else {
                // Unsafe scheme: drop the link, keep the visible text only.
                nodes.push(match[1]);
            }
        } else if (match[3] !== undefined) {
            nodes.push(<strong key={keyPrefix + "-b" + i}>{match[3]}</strong>);
        } else if (match[4] !== undefined) {
            nodes.push(<code key={keyPrefix + "-c" + i}>{match[4]}</code>);
        }
        lastIndex = regex.lastIndex;
        i++;
    }
    if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
    return nodes;
}

// Split a GFM table row ("| a | b |") into trimmed cell strings.
function parseTableRow(line) {
    let s = line.trim();
    if (s.startsWith("|")) s = s.slice(1);
    if (s.endsWith("|")) s = s.slice(0, -1);
    return s.split("|").map((c) => c.trim());
}

// True when a line is a GFM table separator, e.g. "| --- | :--: | ---: |".
function isTableSeparator(line) {
    const s = line.trim();
    if (s.indexOf("-") === -1) return false;
    return /^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?$/.test(s);
}

// Determine column alignment from a separator cell (":--" left, "--:" right, ":-:" center).
function cellAlign(sep) {
    const c = sep.trim();
    const left = c.startsWith(":");
    const right = c.endsWith(":");
    if (left && right) return "center";
    if (right) return "right";
    if (left) return "left";
    return null;
}

// Render a GFM table starting at lines[start]; returns { node, next }.
function renderTable(lines, start, key) {
    const headerCells = parseTableRow(lines[start]);
    const aligns = parseTableRow(lines[start + 1]).map(cellAlign);
    const bodyRows = [];
    let j = start + 2;
    while (j < lines.length && lines[j].trim() !== "" && lines[j].indexOf("|") !== -1) {
        bodyRows.push(parseTableRow(lines[j]));
        j++;
    }
    // Auto right-align columns whose body cells are all numeric (currency/percent allowed).
    const numericCol = headerCells.map((_, c) => {
        if (bodyRows.length === 0) return false;
        return bodyRows.every((r) => {
            const v = (r[c] || "").trim();
            return v === "" || /^[$€£]?-?\d[\d,]*(\.\d+)?%?$/.test(v);
        });
    });
    const alignFor = (c) => aligns[c] || (numericCol[c] ? "right" : "left");
    const node = (
        <table key={"t" + key}>
            <thead>
                <tr>
                    {headerCells.map((h, ci) => (
                        <th key={ci} style={{ textAlign: alignFor(ci) }}>
                            {renderInline(h, "th" + key + "-" + ci)}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {bodyRows.map((r, ri) => (
                    <tr key={ri}>
                        {headerCells.map((_, ci) => (
                            <td key={ci} style={{ textAlign: alignFor(ci) }}>
                                {renderInline(r[ci] || "", "td" + key + "-" + ri + "-" + ci)}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
    return { node, next: j };
}

const HEADING_SIZES = {
    1: "1.25rem",
    2: "1.15rem",
    3: "1.05rem",
    4: "1rem",
    5: "0.95rem",
    6: "0.9rem",
};

function renderMarkdown(text) {
    const lines = String(text || "").split("\n");
    const blocks = [];
    let listItems = null;
    let key = 0;
    const flushList = () => {
        if (listItems) {
            blocks.push(
                <ul key={"ul" + key++} style={{ margin: "4px 0", paddingLeft: 18 }}>
                    {listItems}
                </ul>
            );
            listItems = null;
        }
    };

    let i = 0;
    while (i < lines.length) {
        const line = lines[i];

        // GFM table: a row containing "|" immediately followed by a separator row.
        if (
            line.indexOf("|") !== -1 &&
            i + 1 < lines.length &&
            isTableSeparator(lines[i + 1])
        ) {
            flushList();
            const { node, next } = renderTable(lines, i, key++);
            blocks.push(node);
            i = next;
            continue;
        }

        // ATX heading (# .. ######).
        const heading = line.match(/^\s*(#{1,6})\s+(.*)$/);
        if (heading) {
            flushList();
            const level = heading[1].length;
            blocks.push(
                <Typography
                    key={"h" + key++}
                    component={"h" + Math.min(level + 2, 6)}
                    style={{ fontSize: HEADING_SIZES[level], fontWeight: 600, margin: "8px 0 4px" }}
                >
                    {renderInline(heading[2], "h" + i)}
                </Typography>
            );
            i++;
            continue;
        }

        // Bullet list item.
        const bullet = line.match(/^\s*[-*]\s+(.*)$/);
        if (bullet) {
            if (!listItems) listItems = [];
            listItems.push(<li key={"li" + i}>{renderInline(bullet[1], "li" + i)}</li>);
            i++;
            continue;
        }

        // Blank line ends any open list.
        if (line.trim() === "") {
            flushList();
            i++;
            continue;
        }

        // Default: paragraph.
        flushList();
        blocks.push(
            <p key={"p" + i} style={{ margin: "4px 0" }}>
                {renderInline(line, "p" + i)}
            </p>
        );
        i++;
    }
    flushList();
    return blocks;
}

function isEditableTarget(target) {
    if (!target) return false;

    const tagName = target.tagName ? target.tagName.toLowerCase() : "";
    return tagName === "input" || tagName === "textarea" || target.isContentEditable;
}

const useStyles = makeStyles((theme) => ({
    drawerPaper: { width: 400, display: "flex", flexDirection: "column", height: "100%" },
    resizeHandle: {
        position: "absolute",
        top: 0,
        left: 0,
        height: "100%",
        width: 6,
        cursor: "col-resize",
        zIndex: 1300,
        transition: "background-color 0.15s ease",
        "&:hover": { backgroundColor: theme.palette.primary.main, opacity: 0.5 },
        "&:active": { backgroundColor: theme.palette.primary.main, opacity: 0.7 },
    },
    header: {
        padding: theme.spacing(1),
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    messagesContainer: { flex: 1, overflowY: "auto", padding: theme.spacing(1) },
    messageRow: {
        display: "flex",
        width: "100%",
        marginBottom: theme.spacing(1),
    },
    userRow: {
        justifyContent: "flex-end",
    },
    assistantRow: {
        justifyContent: "flex-start",
    },
    userMessage: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        display: "inline-block",
        borderRadius: 12,
        borderBottomRightRadius: 4,
        padding: theme.spacing(1, 1.5),
        maxWidth: "80%",
        width: "fit-content",
        wordBreak: "break-word",
    },
    assistantMessage: {
        backgroundColor: theme.palette.type === "dark" ? "#424242" : "#e0e0e0",
        color: theme.palette.text.primary,
        display: "inline-block",
        borderRadius: 12,
        borderBottomLeftRadius: 4,
        padding: theme.spacing(1, 1.5),
        maxWidth: "80%",
        width: "fit-content",
        wordBreak: "break-word",
    },
    assistantContent: {
        fontSize: "0.875rem",
        lineHeight: 1.5,
        wordBreak: "break-word",
        "& p:first-child": { marginTop: 0 },
        "& p:last-child": { marginBottom: 0 },
        "& table": {
            borderCollapse: "collapse",
            margin: "8px 0",
            fontSize: "0.8125rem",
            width: "100%",
        },
        "& th, & td": {
            border: "1px solid #ddd",
            padding: "4px 8px",
            verticalAlign: "top",
        },
        "& th": {
            backgroundColor: theme.palette.type === "dark" ? "#333" : "#f5f5f5",
            fontWeight: 600,
        },
        "& code": {
            backgroundColor: theme.palette.type === "dark" ? "#1e1e1e" : "#f0f0f0",
            padding: "1px 4px",
            borderRadius: 3,
            fontFamily: "monospace",
            fontSize: "0.85em",
        },
        "& a": { color: theme.palette.primary.main, wordBreak: "break-all" },
    },
    inputContainer: {
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(1),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    inputField: { flex: 1, marginRight: theme.spacing(1) },
}));

function CopilotSideBar(props) {
    const currentTheme =
        localStorage.getItem("themeName") === "Dark Theme" ? themes.dark : themes.blue;
    const classes = useStyles();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [accessDenied, setAccessDenied] = useState(false);
    const [accessDeniedMessage, setAccessDeniedMessage] = useState("");
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const lastPromptRef = useRef("");

    // Conversation id persists for the whole session (ref avoids re-render churn).
    const conversationIdRef = useRef(null);
    // AbortController for the in-flight turn (used by the Stop button).
    const abortRef = useRef(null);

    // Resizable drawer width (drag the left edge, like VS Code). Persisted across sessions.
    const MIN_WIDTH = 320;
    const MAX_WIDTH = 900;
    const [drawerWidth, setDrawerWidth] = useState(() => {
        const saved = parseInt(localStorage.getItem("copilotWidth"), 10);
        return !isNaN(saved) ? Math.min(Math.max(saved, MIN_WIDTH), MAX_WIDTH) : 400;
    });

    const startResize = (e) => {
        e.preventDefault();
        const onMove = (ev) => {
            const newWidth = Math.min(
                Math.max(window.innerWidth - ev.clientX, MIN_WIDTH),
                MAX_WIDTH
            );
            setDrawerWidth(newWidth);
        };
        const onUp = () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
            setDrawerWidth((w) => {
                localStorage.setItem("copilotWidth", String(w));
                return w;
            });
        };
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
    };

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    useEffect(() => {
        const handleGlobalKeyDown = (event) => {
            if (!props.openCopilot) return;

            if (event.key === "Escape") {
                event.preventDefault();
                handleClose();
                return;
            }

            if (accessDenied || loading) return;
            if (event.key !== "/" || event.ctrlKey || event.altKey || event.metaKey) return;
            if (isEditableTarget(event.target)) return;

            event.preventDefault();
            if (inputRef.current) {
                inputRef.current.focus();
            }
        };

        window.addEventListener("keydown", handleGlobalKeyDown);
        return () => {
            window.removeEventListener("keydown", handleGlobalKeyDown);
        };
    }, [accessDenied, loading, props.openCopilot]);

    const handleSend = async () => {
        const trimmed = input.trim();
        if (!trimmed || loading) return;

        lastPromptRef.current = trimmed;

        const newMessages = [...messages, { role: "user", content: trimmed }];
        setMessages(newMessages);
        setInput("");
        setLoading(true);

        const controller = new AbortController();
        abortRef.current = controller;

        try {
            // Use the logged-in user's alias/MS account as the userId (same in production).
            const userAlias = localStorage.getItem("username") || undefined;

            // Create the conversation once and reuse it (keeps server-side history + summary).
            if (!conversationIdRef.current) {
                conversationIdRef.current = await createConversation(undefined, userAlias);
            }

            // Unique messageId per turn — reusing one returns the cached answer (idempotency).
            const messageId = "m-" + Date.now();

            const response = await postTurn(
                conversationIdRef.current,
                trimmed,
                messageId,
                controller.signal,
                userAlias
            );

            // 204/cancelled -> response is null.
            if (!response) {
                setLoading(false);
                abortRef.current = null;
                return;
            }

            // The server may return the turn nested (`response.turn`) or flat
            // (reply/citations at the top level). Support both shapes.
            const turn = response.turn || response;
            setMessages([
                ...newMessages,
                {
                    role: "assistant",
                    content: turn.reply || "No response received.",
                },
            ]);
        } catch (err) {
            // Abort = user pressed Stop; don't show an error bubble.
            if (err && err.name === "AbortError") {
                return;
            } else if (err && err.status === 403) {
                // User not authorized to use Copilot
                console.warn("Copilot access denied (403):", err.detail);
                setAccessDenied(true);
                setAccessDeniedMessage(err.detail || "You do not have access to Copilot.");
                // Remove the user's message since access is denied
                setMessages(messages);
            } else {
                console.error("Copilot error:", err);
                setMessages([
                    ...newMessages,
                    { role: "assistant", content: "Sorry, something went wrong. Please try again." },
                ]);
            }
        } finally {
            setLoading(false);
            abortRef.current = null;
        }
    };

    const handleStop = () => {
        if (abortRef.current) {
            abortRef.current.abort();
        }
    };

    const handleInputKeyDown = (e) => {
        if (e.key === "ArrowUp" && !input.trim() && lastPromptRef.current) {
            e.preventDefault();
            setInput(lastPromptRef.current);
            return;
        }

        if (e.key === "Enter" && e.ctrlKey) {
            e.preventDefault();
            handleSend();
            return;
        }

        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleClose = () => {
        props.setOpenCopilot(false);
    };

    return (
        <ThemeProvider theme={currentTheme}>
            <SwipeableDrawer
                anchor="right"
                open={props.openCopilot}
                onOpen={() => {}}
                onClose={handleClose}
                PaperProps={{ className: classes.drawerPaper, style: { width: drawerWidth } }}
            >
                <div
                    className={classes.resizeHandle}
                    onMouseDown={startResize}
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize Copilot panel"
                />
                <Box
                    className={classes.header}
                    style={{
                        backgroundColor:
                            localStorage.getItem("themeName") === "Dark Theme"
                                ? currentTheme.palette.background.default
                                : currentTheme.palette.primary.main,
                        color:
                            localStorage.getItem("themeName") === "Dark Theme"
                                ? currentTheme.palette.text.primary
                                : currentTheme.palette.primary.contrastText,
                    }}
                >
                    <Typography variant="h6">
                        <b>Mint Studio Copilot</b>
                    </Typography>
                    <IconButton color="inherit" onClick={handleClose} aria-label="close" size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>

                <div className={classes.messagesContainer}>
                    {accessDenied ? (
                        <Box textAlign="center" mt={4} p={2}>
                            <Typography variant="h6" color="error" gutterBottom>
                                Access Denied
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {accessDeniedMessage}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {messages.length === 0 && (
                                <Box textAlign="center" mt={4} px={2}>
                                    <Typography variant="h6" gutterBottom>
                                        👋 Hi, I'm Mint Studio Copilot
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                        Your AI assistant for Mint Studio. Ask me a question and
                                        I'll help you find answers.
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        display="block"
                                        style={{ marginTop: 12 }}
                                    >
                                        Try asking:
                                    </Typography>
                                    {[
                                        "How are territory assignments done?",
                                        "Get me the deatils of the pending CRs in ANZ region.",
                                        "How do I get started in Mint Studio?",
                                    ].map((suggestion) => (
                                        <Box
                                            key={suggestion}
                                            onClick={() => setInput(suggestion)}
                                            style={{
                                                cursor: "pointer",
                                                border: `1px solid ${currentTheme.palette.divider}`,
                                                borderRadius: 16,
                                                padding: "6px 12px",
                                                margin: "6px auto 0",
                                                maxWidth: "90%",
                                                fontSize: "0.8125rem",
                                            }}
                                        >
                                            {suggestion}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            {messages.map((msg, idx) => (
                                <div
                                    key={idx}
                                    className={
                                        msg.role === "user"
                                            ? `${classes.messageRow} ${classes.userRow}`
                                            : `${classes.messageRow} ${classes.assistantRow}`
                                    }
                                >
                                    <div
                                        className={
                                            msg.role === "user"
                                                ? classes.userMessage
                                                : classes.assistantMessage
                                        }
                                    >
                                        {msg.role === "assistant" ? (
                                            <div className={classes.assistantContent}>
                                                {renderMarkdown(stripCitations(msg.content))}
                                            </div>
                                        ) : (
                                            <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                                                {msg.content}
                                            </Typography>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {loading && (
                                <Box display="flex" justifyContent="flex-end" mb={1}>
                                    <CircularProgress size={20} />
                                </Box>
                            )}
                        </>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                <div className={classes.inputContainer}>
                    <TextField
                        className={classes.inputField}
                        variant="outlined"
                        size="small"
                        placeholder="Type a message..."
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleInputKeyDown}
                        multiline
                        rowsMax={3}
                        disabled={loading || accessDenied}
                        inputRef={inputRef}
                        autoFocus
                    />
                    {loading ? (
                        <IconButton color="secondary" onClick={handleStop} aria-label="Stop">
                            <StopIcon />
                        </IconButton>
                    ) : (
                        <IconButton
                            color="primary"
                            onClick={handleSend}
                            disabled={!input.trim() || accessDenied}
                            aria-label="Send message"
                        >
                            <SendIcon />
                        </IconButton>
                    )}
                </div>
            </SwipeableDrawer>
        </ThemeProvider>
    );
}

export default CopilotSideBar;